import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FYLKER } from '../components/LocationSelector';
import { KATEGORIER } from '../components/CategorySelector';
import JobCard from '../components/JobCard';
import SwipeableJobCard from '../components/SwipeableJobCard';
import { useAuth } from '../context/AuthContext';
import { isDemoMode } from '../demo/useDemoMode';
import { filterJobs } from '../demo/mockData';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [selectedFylke, setSelectedFylke] = useState(searchParams.get('fylke') || null);
  const [selectedKategori, setSelectedKategori] = useState(searchParams.get('category') || null);
  const [minPrice, setMinPrice] = useState(searchParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchJobs = async () => {
    setLoading(true);
    
    // In demo mode, use mock data
    if (isDemoMode()) {
      const filters = {
        q: searchParams.get('q') || undefined,
        fylke: searchParams.get('fylke') || undefined,
        category: searchParams.get('category') || undefined,
        minPrice: searchParams.get('minPrice') || undefined,
        maxPrice: searchParams.get('maxPrice') || undefined
      };
      
      const mockJobs = filterJobs(filters);
      setJobs(mockJobs);
      setTotalResults(mockJobs.length);
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams();
      
      const q = searchParams.get('q');
      const fylke = searchParams.get('fylke');
      const category = searchParams.get('category');
      const min = searchParams.get('minPrice');
      const max = searchParams.get('maxPrice');
      
      if (q) params.append('q', q);
      if (fylke) params.append('fylke', fylke);
      if (category) params.append('category', category);
      if (min) params.append('minPrice', min);
      if (max) params.append('maxPrice', max);
      params.append('limit', '50');
      
      const response = await axios.get(`${API_URL}/jobs/search/query?${params.toString()}`);
      setJobs(response.data || []);
      setTotalResults(response.data?.length || 0);
    } catch (error) {
      console.error('Feil ved søk:', error);
      setJobs([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateSearchParams();
  };

  const updateSearchParams = () => {
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.set('q', searchTerm.trim());
    if (selectedFylke) params.set('fylke', selectedFylke);
    if (selectedKategori) params.set('category', selectedKategori);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedFylke(null);
    setSelectedKategori(null);
    setMinPrice('');
    setMaxPrice('');
    setSearchParams(new URLSearchParams());
  };

  const handleJobContact = (job) => {
    if (isAuthenticated) {
      navigate(`/meldinger?job=${job.id}&user=${job.poster?.id}`);
    } else {
      navigate('/logg-inn');
    }
  };

  const handleJobSave = (job) => {
    console.log('Lagre jobb:', job.id);
  };

  const activeFilterCount = [selectedFylke, selectedKategori, minPrice, maxPrice].filter(Boolean).length;

  const getFylkeName = (id) => FYLKER.find(f => f.id === id)?.name || id;
  const getKategoriName = (id) => KATEGORIER.find(k => k.id === id)?.name || id;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Search Header */}
      <div className="bg-white border-b border-slate-200 sticky top-14 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">
                search
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Søk etter oppdrag..."
                className="w-full pl-10 pr-4 py-2.5 text-[#0A1929] bg-slate-50 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent transition-all text-sm"
              />
            </div>
            <button
              type="submit"
              className="bg-[#0A1929] text-white px-4 py-2.5 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors"
            >
              <span className="material-symbols-outlined">search</span>
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`relative px-4 py-2.5 rounded-lg font-medium transition-colors ${
                showFilters || activeFilterCount > 0
                  ? 'bg-[#1E3A5F] text-white'
                  : 'bg-slate-100 text-[#0A1929] hover:bg-slate-200'
              }`}
            >
              <span className="material-symbols-outlined">tune</span>
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </form>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 pb-4 border-t border-slate-100 pt-4 space-y-4">
              {/* Fylke */}
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">Fylke</label>
                <select
                  value={selectedFylke || ''}
                  onChange={(e) => setSelectedFylke(e.target.value || null)}
                  className="w-full md:w-auto px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                >
                  <option value="">Alle fylker</option>
                  {FYLKER.map(fylke => (
                    <option key={fylke.id} value={fylke.id}>{fylke.name}</option>
                  ))}
                </select>
              </div>

              {/* Kategori */}
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">Kategori</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedKategori(null)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      !selectedKategori
                        ? 'bg-[#0A1929] text-white'
                        : 'bg-slate-100 text-[#0A1929] hover:bg-slate-200'
                    }`}
                  >
                    Alle
                  </button>
                  {KATEGORIER.map(kat => (
                    <button
                      key={kat.id}
                      type="button"
                      onClick={() => setSelectedKategori(kat.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1 ${
                        selectedKategori === kat.id
                          ? 'bg-[#0A1929] text-white'
                          : 'bg-slate-100 text-[#0A1929] hover:bg-slate-200'
                      }`}
                    >
                      <span className="material-symbols-outlined text-sm">{kat.icon}</span>
                      {kat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prisrange */}
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">Prisrange</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    placeholder="Min"
                    className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                  />
                  <span className="text-slate-400">—</span>
                  <input
                    type="number"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    placeholder="Max"
                    className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
                  />
                  <span className="text-sm text-slate-500">kr</span>
                </div>
              </div>

              {/* Filter Actions */}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={updateSearchParams}
                  className="bg-[#0A1929] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1E3A5F] transition-colors"
                >
                  Bruk filter
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-slate-500 px-4 py-2 rounded-lg text-sm font-medium hover:text-[#0A1929] transition-colors"
                >
                  Nullstill
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Active Filters Tags */}
      {activeFilterCount > 0 && (
        <div className="bg-white border-b border-slate-100 px-4 py-2">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">Aktive filter:</span>
            {selectedFylke && (
              <span className="inline-flex items-center gap-1 bg-[#0A1929] text-white text-xs px-2 py-1 rounded-full">
                <span className="material-symbols-outlined text-xs">location_on</span>
                {getFylkeName(selectedFylke)}
                <button onClick={() => { setSelectedFylke(null); updateSearchParams(); }} className="ml-1 hover:text-blue-200">
                  <span className="material-symbols-outlined text-xs">close</span>
                </button>
              </span>
            )}
            {selectedKategori && (
              <span className="inline-flex items-center gap-1 bg-[#1E3A5F] text-white text-xs px-2 py-1 rounded-full">
                {getKategoriName(selectedKategori)}
                <button onClick={() => { setSelectedKategori(null); updateSearchParams(); }} className="ml-1 hover:text-blue-200">
                  <span className="material-symbols-outlined text-xs">close</span>
                </button>
              </span>
            )}
            {(minPrice || maxPrice) && (
              <span className="inline-flex items-center gap-1 bg-slate-600 text-white text-xs px-2 py-1 rounded-full">
                {minPrice || '0'} - {maxPrice || '∞'} kr
                <button onClick={() => { setMinPrice(''); setMaxPrice(''); updateSearchParams(); }} className="ml-1 hover:text-blue-200">
                  <span className="material-symbols-outlined text-xs">close</span>
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">
            {loading ? 'Søker...' : `${totalResults} oppdrag funnet`}
          </p>
        </div>

        {/* Jobs Grid/List */}
        {loading ? (
          <>
            {/* Mobile loading skeleton */}
            <div className="md:hidden space-y-0">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex bg-white border-b border-slate-100 animate-pulse">
                  <div className="w-28 h-28 bg-slate-200"></div>
                  <div className="flex-1 p-3 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                    <div className="h-5 bg-slate-200 rounded w-1/4 mt-4"></div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Desktop loading skeleton */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden animate-pulse">
                  <div className="aspect-video bg-slate-200"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                    <div className="h-5 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-4 bg-slate-200 rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : jobs.length > 0 ? (
          <>
            {/* Mobile list view */}
            <div className="md:hidden bg-white rounded-xl overflow-hidden shadow-sm">
              {jobs.map((job) => (
                <SwipeableJobCard
                  key={job.id}
                  job={job}
                  onContact={() => handleJobContact(job)}
                  onSave={() => handleJobSave(job)}
                >
                  <JobCard job={job} isMobileLayout={true} />
                </SwipeableJobCard>
              ))}
            </div>
            
            {/* Desktop grid view */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6">
              {jobs.map((job) => (
                <JobCard key={job.id} job={job} isMobileLayout={false} />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16 px-4 bg-white rounded-xl shadow-sm">
            <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block">
              search_off
            </span>
            <h3 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
              Ingen oppdrag funnet
            </h3>
            <p className="text-slate-500 mb-4">
              Prøv å endre søkeord eller fjerne noen filter
            </p>
            <button 
              onClick={clearFilters}
              className="bg-[#0A1929] text-white px-6 py-2 rounded-lg hover:bg-[#1E3A5F] transition-colors"
            >
              Nullstill søk
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
